import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Drawer, Field, notify } from '@/kit/ui'
import { apiRootOf, fileNameFor } from '@/lib/endpoints'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore, type CreateApiInput } from '@/store/codeStore'
import type { AuthKind, HttpMethod, Lender } from '@/types'
import { playgroundUrl } from '../links'
import { ofCode } from '@/lib/batch'

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const AUTH: { value: AuthKind; label: string }[] = [
  { value: 'bearer', label: 'bearer (LENDER_TOKEN from the token-source endpoint)' },
  { value: 'api_key_header', label: 'api-key header (LENDER_API_KEY)' },
  { value: 'basic', label: 'basic auth (LENDER_BASIC_AUTH)' },
  { value: 'none', label: 'none' },
  { value: 'token_source', label: 'token source (this endpoint gives the bearer token)' },
]

const EMPTY: CreateApiInput = { name: '', endpoint: '', method: 'POST', path: '/', auth: 'bearer', authNote: '', whitelist: { state: 'done', date: null }, usedIn: '—' }

/**
 * Create API (F2-2): the record + the endpoint file `src/api/<route>.js` (one REST endpoint, defineApi with an empty
 * handler that calls the lender) + one test event, in the draft of the chosen product's batch (BP-1; a new version from
 * its live one if there is no draft). Then the playground opens on the file.
 */
export function CreateApiDrawer({ lender, productSlug, productLabel, isOpen, onClose }: { lender: Lender; productSlug: string | null; productLabel: string; isOpen: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const createApi = useCodeStore((s) => s.createApi)
  const batches = useCodeStore((s) => s.batches)
  const [form, setForm] = useState<CreateApiInput>(EMPTY)
  const [routeTouched, setRouteTouched] = useState(false)
  const [error, setError] = useState<string>()

  const mine = ofCode(batches, lender.slug, productSlug)
  const draft = mine.filter((b) => b.status === 'draft' || b.status === 'tests_passed').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  const live = mine.find((b) => b.status === 'live')
  const target = draft ?? live
  const apiRoot = apiRootOf(target?.files ?? [])
  const prefix = (() => {
    const versions = (target?.manifest.endpoints ?? []).map((e) => /^\/(v[\d.]+)\//.exec(e.path)?.[1]).filter((v): v is string => !!v)
    return versions.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0] ?? 'v1'
  })()
  useEffect(() => {
    if (!isOpen) return
    setForm(EMPTY)
    setRouteTouched(false)
    setError(undefined)
  }, [isOpen])

  const targetText = `${productLabel ? `${productLabel}: ` : ''}${draft ? `draft batch ${draft.version}` : live ? `a new version from ${live.version}` : 'a new batch 1.0.0'}`
  const valid = form.name.trim().length >= 3 && /^\/[a-z0-9][a-z0-9/-]*[a-z0-9]$/.test(form.endpoint) && form.path.startsWith('/')
  const taken = !!target?.manifest.endpoints.some((e) => e.path === form.endpoint)

  const submit = () => {
    try {
      const batchId = createApi(lender.slug, { ...form, name: form.name.trim(), authNote: form.authNote.trim() || AUTH.find((a) => a.value === form.auth)!.label.split(' (')[0]!, productSlug }, user.email)
      const b = useCodeStore.getState().batches.find((x) => x.id === batchId)!
      notify.success(`${form.name} created`, `${form.method} ${form.endpoint} → ${apiRoot}${form.endpoint}.js and its test event in batch ${b.version}; one commit.`)
      onClose()
      navigate(playgroundUrl(lender.slug, b.version, `${apiRoot}${form.endpoint}.js`, b.productSlug ?? null))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Create API"
      subtitle={`${lender.name} · goes into ${targetText}`}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || taken} onClick={submit} data-testid="create-api">Create endpoint + test, open playground</button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="API name" required help="As the lender calls it, e.g. Soft offer">
          {(id) => <input id={id} className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, endpoint: routeTouched ? form.endpoint : `/${prefix}/${fileNameFor(e.target.value).replace(/\.js$/, '')}` })} placeholder="Soft offer" />}
        </Field>
        <Field label="Our endpoint (route)" required help={`The file ${apiRoot}${form.endpoint || '/…'}.js; folders are free, e.g. /v2.0.0/get-offer`} error={taken ? 'This route already exists in the batch' : undefined}>
          {(id) => <input id={id} className="input mono" value={form.endpoint} onChange={(e) => { setRouteTouched(true); setForm({ ...form, endpoint: e.target.value }) }} placeholder={`/${prefix}/soft-offer`} />}
        </Field>
        <Field label="Lender's method" required>
          {(id) => <select id={id} className="input" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as HttpMethod })}>{METHODS.map((m) => <option key={m}>{m}</option>)}</select>}
        </Field>
        <Field label="Lender's path" required help="Relative to LENDER_BASE_URL; {id} for path parameters">
          {(id) => <input id={id} className="input mono" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} placeholder="/lead/{id}/soft-offer" />}
        </Field>
        <Field label="Auth" required>
          {(id) => <select id={id} className="input" value={form.auth} onChange={(e) => setForm({ ...form, auth: e.target.value as AuthKind })}>{AUTH.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</select>}
        </Field>
        <Field label="Auth note" help="e.g. partner_id + secret → bearer 55 min">
          {(id) => <input id={id} className="input" value={form.authNote} onChange={(e) => setForm({ ...form, authNote: e.target.value })} />}
        </Field>
        <Field label="IP whitelist at the lender">
          {(id) => (
            <select id={id} className="input" value={form.whitelist.state} onChange={(e) => setForm({ ...form, whitelist: { ...form.whitelist, state: e.target.value as CreateApiInput['whitelist']['state'] } })}>
              <option value="done">done (our IPs confirmed)</option>
              <option value="requested">requested</option>
              <option value="not_needed">not needed</option>
            </select>
          )}
        </Field>
        <Field label="Confirmed on">
          {(id) => <input id={id} type="date" className="input" value={form.whitelist.date ?? ''} onChange={(e) => setForm({ ...form, whitelist: { ...form.whitelist, date: e.target.value || null } })} />}
        </Field>
      </div>
      <Field label="Used in" help="Which workflows will call it; the canvas fills this in later">
        {(id) => <input id={id} className="input" value={form.usedIn} onChange={(e) => setForm({ ...form, usedIn: e.target.value })} />}
      </Field>
      {error && <p className="field-error">{error}</p>}
      <p className="mt-3 text-2xs text-muted">The file starts as <code>defineApi({'{'} outcomes: ['ok', 'error'], handler {'}'})</code> with a <code>ctx.lender()</code> call typed with our definitions, plus one test event “Reply 200 → ok”. Keys are never in the code: <code>ctx.env.NAME</code> / <code>ctx.secret('NAME')</code> come from the Env variables tab (any new name appears there by itself). Every call and outcome must pass the system check before the batch is approved.</p>
    </Drawer>
  )
}
